import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LOGO_SRC } from "../constants/branding";

const NAV_ITEMS = [
  { label: "About", sectionId: "services", key: "about" },
  { label: "Pricing", sectionId: "pricing", key: "pricing" },
  { label: "Solana gRPC", routeHref: "/solana", key: "solana" },
  // Docs route is in-app now (src/Pages/Documentation.jsx). Was previously
  // pointing at DOCS_URL external doc site (placeholder, not maintained).
  { label: "Docs", routeHref: "/docs", key: "docs" },
];

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState("about");

  const isLanding = location.pathname === "/";

  useEffect(() => {
    if (!isLanding) {
      return;
    }

    const scrollItems = NAV_ITEMS.filter((item) => item.sectionId);

    const pickActive = () => {
      const viewportAnchor = window.scrollY + window.innerHeight * 0.28;

      if (window.scrollY < 120) {
        setActiveKey(scrollItems[0]?.key || "");
        return;
      }

      for (let i = scrollItems.length - 1; i >= 0; i -= 1) {
        const el = document.getElementById(scrollItems[i].sectionId);
        if (!el) {
          continue;
        }
        if (viewportAnchor >= el.offsetTop) {
          setActiveKey(scrollItems[i].key);
          return;
        }
      }
    };

    pickActive();
    window.addEventListener("scroll", pickActive, { passive: true });
    return () => window.removeEventListener("scroll", pickActive);
  }, [isLanding]);

  const scrollToSection = (targetId) => {
    const section = document.getElementById(targetId);
    if (!section) {
      return;
    }
    const fixedHeaderOffset = 72;
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: Math.max(0, sectionTop - fixedHeaderOffset),
      behavior: "smooth",
    });
  };

  const handleSectionNav = (event, targetId, key) => {
    event.preventDefault();
    setActiveKey(key);

    const runScroll = () => scrollToSection(targetId);

    if (!isLanding) {
      navigate("/");
      window.setTimeout(runScroll, 350);
      return;
    }

    runScroll();
  };

  return (
    <div className="relative">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/[0.06] bg-[#131313]">
        <div className="mx-auto flex h-[60px] items-center justify-between px-6 sm:h-[64px] sm:px-10">
          <div className="flex min-w-0 items-center gap-8 lg:gap-12">
            <Link to="/" className="relative z-50 shrink-0">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="h-[32px] w-auto sm:h-[36px]"
              />
            </Link>

            <nav
              className="font-secondary hidden items-center gap-8 text-[14px] font-medium md:flex lg:gap-10"
              aria-label="Primary"
            >
              {NAV_ITEMS.map(({ label, sectionId, externalHref, routeHref, key }) => {
                const isActive = activeKey === key;
                const baseClass = `relative pb-1 transition-colors ${
                  isActive ? "text-primary" : "text-white hover:text-white/90"
                }`;
                if (externalHref) {
                  return (
                    <a
                      key={key}
                      href={externalHref}
                      target="_blank"
                      rel="noreferrer"
                      className={baseClass}
                    >
                      {label}
                    </a>
                  );
                }
                if (routeHref) {
                  return (
                    <Link key={key} to={routeHref} className={baseClass}>
                      {label}
                    </Link>
                  );
                }
                return (
                  <a
                    key={key}
                    href={`#${sectionId}`}
                    onClick={(e) => handleSectionNav(e, sectionId, key)}
                    className={baseClass}
                  >
                    {label}
                    {isActive ? (
                      <span
                        className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary"
                        aria-hidden
                      />
                    ) : null}
                  </a>
                );
              })}
            </nav>
          </div>

          <div className="font-secondary flex shrink-0 items-center gap-4 sm:gap-5">
            <Link
              to="/login"
              className="text-[14px] text-white transition-colors hover:text-white/85"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="rounded-sm border border-primary bg-primary px-6 py-2.5 text-[14px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover sm:px-5"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      <div className="h-[60px] sm:h-[64px]" aria-hidden />

      <section>
        <Outlet />
      </section>
    </div>
  );
};

export default Navbar;
